import { useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../Context/GlobalContext";

const useDining = () => {
  const { userID, api } = useContext(GlobalContext);

  const getTablesCacheKey = () => `restaurant_tables_cache_${userID}`;
  const getRoomsCacheKey = () => `restaurant_rooms_cache_${userID}`;

  const readCache = (key) => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      sessionStorage.removeItem(key);
      return null;
    }
  };

  const writeCache = (key, data) => {
    sessionStorage.setItem(key, JSON.stringify(data || []));
  };

  const [tables, setTables] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTables = async ({ force = false, silent = false } = {}) => {
    const cacheKey = getTablesCacheKey();

    try {
      if (!silent) {
        setLoading(true);
      }

      // force true hua to cache read nahi hogi
      if (!force) {
        const cachedTables = readCache(cacheKey);

        if (Array.isArray(cachedTables)) {
          setTables(cachedTables);
          return cachedTables;
        }
      }

      const response = await api.post("/Order/getRestaurantTable", {
        userid: userID,
      });

      if (response.data?.responseCode === "00") {
        const data = Array.isArray(response.data?.response)
          ? response.data.response
          : [];

        setTables(data);

        // Fresh API response se old cache replace hogi
        writeCache(cacheKey, data);

        return data;
      }

      setTables([]);
      writeCache(cacheKey, []);

      if (!silent) {
        toast.error(
          response.data?.responseMessage || "Failed to fetch tables",
          {
            toastId: "fetch-tables-error",
          },
        );
      }

      return [];
    } catch (error) {
      console.error("Error fetching tables:", error);

      if (!silent) {
        toast.error("Failed to fetch tables", {
          toastId: "fetch-tables-error",
        });
      }

      return [];
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const addTable = async (tableNumber) => {
    try {
      const requestBody = {
        userid: userID,
        tableNo: tableNumber,
      };

      const response = await api.post("/Order/addRestaurantTable", requestBody);

      if (response.data.responseCode === "00") {
        toast.success(
          response.data.responseMessage || "Table added successfully",
        );
        await fetchTables({ force: true });
        return response.data;
      }

      toast.error(response.data.responseMessage || "Failed to add table");
      return null;
    } catch (error) {
      console.error("ADD TABLE ERROR:", error);
      toast.error(
        error?.response?.data?.responseMessage || "Error adding table",
      );
      return null;
    }
  };

  const deleteTable = async (tableNo) => {
    try {
      const res = await api.post("/Order/v1.0/deleteDiningTable", {
        userid: userID,
        tableNo: String(tableNo),
      });

      if (res?.data?.responseCode === "00") {
        toast.success(
          res?.data?.responseMessage || "Table deleted successfully",
        );
        await fetchTables({ force: true });
        return res.data;
      }

      toast.error(res?.data?.responseMessage || "Failed to delete table");
      return null;
    } catch (error) {
      console.error("DELETE TABLE ERROR:", error);
      toast.error(
        error?.response?.data?.responseMessage || "Something went wrong",
      );
      return null;
    }
  };

  // Room APIs
  const fetchRooms = async ({ force = false, silent = false } = {}) => {
    const cacheKey = getRoomsCacheKey();

    try {
      if (!silent) {
        setLoading(true);
      }

      // force false par hi cache use hogi
      if (!force) {
        const cachedRooms = readCache(cacheKey);

        if (Array.isArray(cachedRooms)) {
          setRooms(cachedRooms);
          return cachedRooms;
        }
      }

      const response = await api.post("/Order/v1.0/getRestaurantRooms", {
        userid: userID,
      });

      if (response.data?.responseCode === "00") {
        const data = Array.isArray(response.data?.response)
          ? response.data.response
          : [];

        setRooms(data);

        // Fresh API response se cache replace hogi
        writeCache(cacheKey, data);

        return data;
      }

      setRooms([]);
      writeCache(cacheKey, []);

      if (!silent) {
        // toast.error(response.data?.responseMessage || "Failed to fetch rooms", {
        //   toastId: "fetch-rooms-error",
        // });
      }

      return [];
    } catch (error) {
      console.error("Error fetching rooms:", error);

      if (!silent) {
        toast.error("Failed to fetch rooms", {
          toastId: "fetch-rooms-error",
        });
      }

      return [];
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const addRoom = async (roomNumber) => {
    try {
      const requestBody = {
        userid: userID,
        roomNumber: roomNumber,
      };

      const response = await api.post(
        "/Order/v1.0/addRestaurantRoom",
        requestBody,
      );

      if (response.data.responseCode === "00") {
        toast.success(
          response.data.responseMessage || "Room added successfully",
        );
        await fetchRooms({ force: true });
        return response.data;
      }

      toast.error(response.data.responseMessage || "Failed to add room");
      return null;
    } catch (error) {
      console.error("ADD ROOM ERROR:", error);
      toast.error(
        error?.response?.data?.responseMessage || "Error adding room",
      );
      return null;
    }
  };

  const refreshDiningCache = async () => {
    try {
      const [freshTables, freshRooms] = await Promise.all([
        fetchTables({ force: true, silent: true }),
        fetchRooms({ force: true, silent: true }),
      ]);

      window.dispatchEvent(
        new CustomEvent("dining-cache-refreshed", {
          detail: {
            tables: freshTables,
            rooms: freshRooms,
          },
        }),
      );

      return {
        tables: freshTables,
        rooms: freshRooms,
      };
    } catch (error) {
      console.error("REFRESH DINING CACHE ERROR:", error);
      return {
        tables: [],
        rooms: [],
      };
    }
  };

  const clearTablesCache = () => {
    const cacheKey = getTablesCacheKey();

    sessionStorage.removeItem(cacheKey);
    setTables([]);
  };

  const clearRoomsCache = () => {
    const cacheKey = getRoomsCacheKey();

    sessionStorage.removeItem(cacheKey);
    setRooms([]);
  };

  useEffect(() => {
    const handleDiningCacheRefreshed = (event) => {
      const freshTables = event?.detail?.tables;
      const freshRooms = event?.detail?.rooms;

      if (Array.isArray(freshTables)) {
        setTables(freshTables);
      }

      if (Array.isArray(freshRooms)) {
        setRooms(freshRooms);
      }
    };

    window.addEventListener(
      "dining-cache-refreshed",
      handleDiningCacheRefreshed,
    );

    return () => {
      window.removeEventListener(
        "dining-cache-refreshed",
        handleDiningCacheRefreshed,
      );
    };
  }, []);

  return {
    tables,
    rooms,
    loading,
    setRooms,
    setLoading,

    clearTablesCache,
    clearRoomsCache,

    fetchTables,
    addTable,
    deleteTable,

    fetchRooms,
    addRoom,

    refreshDiningCache,
  };
};

export default useDining;
